/* eslint-disable react/prop-types */
import { useState } from 'react'
import { AlertTriangle, X } from 'react-feather'
import styled from 'styled-components'

const ValidationAlert = styled.div`
  background: ${({ theme }) =>
    `linear-gradient(135deg, ${theme.deprecated_red1}15 0%, ${theme.deprecated_red1}08 100%)`};
  border: 1px solid ${({ theme }) => theme.deprecated_red1}40;
  border-radius: 4px;
  padding: 16px;
  position: relative;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px ${({ theme }) => theme.deprecated_red1}20;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const AlertHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`

const AlertTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.deprecated_red1};
`

const AlertIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ theme }) => theme.deprecated_red1}25;

  svg {
    width: 12px;
    height: 12px;
    color: ${({ theme }) => theme.deprecated_red1};
  }
`

const DismissButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    background: ${({ theme }) => theme.deprecated_red1}15;
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.deprecated_red1};
  }
`

const ErrorList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const ErrorItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.deprecated_red1};
  padding: 4px 0;
`

const ErrorBullet = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${({ theme }) => theme.deprecated_red1};
  margin-top: 6px;
  flex-shrink: 0;
`

const ErrorText = styled.div`
  flex: 1;
  word-break: break-word;
  color: ${({ theme }) => theme.deprecated_red2};
  opacity: 0.8;
`

const ValidationTypeChip = styled.div<{ type: string }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-left: 8px;

  ${({ type, theme }) => {
    switch (type) {
      case 'kodiakx':
        return `
          background: ${theme.deprecated_primary1}25;
          color: ${theme.deprecated_primary1};
          border: 1px solid ${theme.deprecated_primary1}40;
        `
      case 'kodiak-native':
        return `
          background: ${theme.deprecated_blue1}25;
          color: ${theme.deprecated_blue1};
          border: 1px solid ${theme.deprecated_blue1}40;
        `
      default:
        return `
          background: ${theme.textSecondary}25;
          color: ${theme.textSecondary};
          border: 1px solid ${theme.textSecondary}40;
        `
    }
  }}
`

const WarningSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid ${({ theme }) => theme.deprecated_red1}30;
`

const WarningTitle = styled.div`
  color: ${({ theme }) => theme.deprecated_yellow1};
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
`

const WarningItem = styled(ErrorItem)`
  color: ${({ theme }) => theme.deprecated_yellow1};
`

const WarningBullet = styled(ErrorBullet)`
  background: ${({ theme }) => theme.deprecated_yellow1};
`

const WarningText = styled(ErrorText)`
  color: ${({ theme }) => theme.textPrimary};
  opacity: 0.85;
`

interface ValidationWarningProps {
  validation: {
    isValid: boolean
    errors: string[]
    warnings?: string[]
    quoteType: string
  }
  onDismiss?: () => void
  className?: string
}

export const ValidationWarning: React.FC<ValidationWarningProps> = ({ validation, onDismiss, className }) => {
  const [isDismissed, setIsDismissed] = useState(false)

  const handleDismiss = () => {
    setIsDismissed(true)
    onDismiss?.()
  }

  if (isDismissed || validation.isValid) {
    return null
  }

  return (
    <ValidationAlert className={className}>
      <AlertHeader>
        <AlertTitle>
          <AlertIcon>
            <AlertTriangle />
          </AlertIcon>
          Transaction Validation Failed
          <ValidationTypeChip type={validation.quoteType}>{validation.quoteType}</ValidationTypeChip>
        </AlertTitle>
        {onDismiss && (
          <DismissButton onClick={handleDismiss}>
            <X />
          </DismissButton>
        )}
      </AlertHeader>

      <ErrorList>
        {validation.errors.map((error, index) => (
          <ErrorItem key={index}>
            <ErrorBullet />
            <ErrorText>{error}</ErrorText>
          </ErrorItem>
        ))}
      </ErrorList>

      {validation.warnings && validation.warnings.length > 0 && (
        <WarningSection>
          <WarningTitle>Additional Warnings:</WarningTitle>
          {validation.warnings.map((warning, index) => (
            <WarningItem key={index}>
              <WarningBullet />
              <WarningText>{warning}</WarningText>
            </WarningItem>
          ))}
        </WarningSection>
      )}
    </ValidationAlert>
  )
}
